import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, MapPin, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, getToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { SecondScan, markIngresso } from "../components/ParkGate";

const STATO = { prenotato: "Autorizzato", effettuato: "ACCESSO EFFETTUATO", uscito: "Veicolo uscito (validità scaduta)", "non effettuato": "Validità scaduta, accesso non effettuato" };

const Row = ({ label, value, testid }) => (
  <div className="py-3 border-b border-slate-100 last:border-0">
    <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">{label}</p>
    <p className="text-lg font-semibold text-[var(--navy)] break-words" data-testid={testid}>{value}</p>
  </div>
);

function GateAction({ r, setR }) {
  const [gate, setGate] = useState(null);
  const [fresh, setFresh] = useState(false);
  useEffect(() => {
    if (!getToken()) { setGate(false); return; }
    api("/web/parking/me").then((m) => setGate(m.can_gate)).catch(() => setGate(false));
  }, []);
  if (gate && !fresh && r.stato === "effettuato") return <SecondScan r={r} />;
  if (r.stato !== "prenotato" || gate === null) return null;
  if (!gate) {
    return getToken() ? <p className="text-sm text-slate-500 text-center" data-testid="pass-not-gate">Solo la portineria può registrare l'accesso.</p> : null;
  }
  if (!r.targa) return <p className="text-sm text-amber-800 bg-amber-50 rounded-xl p-3 text-center font-medium" data-testid="pass-gate-no-plate">Targa mancante: l'accesso si potrà convalidare dopo che l'ospite avrà inserito la targa.</p>;
  const go = async () => { const x = await markIngresso(r); if (x) { setFresh(true); setR({ ...r, ...x }); } };
  return <Button onClick={go} className="w-full h-14 rounded-full bg-green-600 hover:bg-green-700 text-base font-bold tracking-wide" data-testid="pass-confirm"><CheckCircle2 className="w-5 h-5 mr-2" />CONVALIDA ACCESSO</Button>;
}

function PlateEdit({ r }) {
  const [open, setOpen] = useState(!r.targa);
  const [t, setT] = useState(r.targa || "");
  const [busy, setBusy] = useState(false);
  if (r.stato !== "prenotato") return null;
  const save = async () => {
    setBusy(true);
    try { await api(`/web/parking/pass/${r.id}/targa`, { method: "PUT", body: { targa: t } }); toast.success("Targa registrata"); window.location.replace(`/accesso-parcheggio/${r.id}`); }
    catch (e) { toast.error(e.message); setBusy(false); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--teal)] underline" data-testid="pass-plate-edit"><Pencil className="w-3.5 h-3.5" />Inserisci targa</button>;
  return (
    <div className="mt-2 flex gap-2" data-testid="pass-plate-form">
      <Input value={t} onChange={(e) => setT(e.target.value)} className="uppercase font-mono" placeholder="Es. AB123CD" autoFocus data-testid="pass-plate-input" />
      <Button onClick={save} disabled={busy || !t.trim()} className="rounded-full bg-[var(--teal)]" data-testid="pass-plate-save">Salva</Button>
    </div>
  );
}

export default function ParkPass() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { sessionStorage.removeItem("after_login"); api(`/web/parking/pass/${id}`).then(setR).catch((e) => setErr(e.message)); }, [id]);
  const done = r && r.stato !== "prenotato";
  return (
    <div className="min-h-screen bg-[var(--navy-deep)] flex flex-col items-center px-5 py-10">
      <img src="/logo_white.png" alt="Porto Turistico di Roma" className="h-14 w-auto mb-8" data-testid="pass-logo" />
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl space-y-5" data-testid="park-pass">
        <p className="text-xs uppercase tracking-[0.2em] text-[var(--teal)] font-bold" data-testid="pass-title">Autorizzazione parcheggio{r?.messaggio_nome ? ` - ${r.messaggio_nome}` : ""}</p>
        {err && <p className="text-red-600 font-semibold" data-testid="pass-error">{err}</p>}
        {!r && !err && <p className="text-slate-500">Caricamento…</p>}
        {r && <>
          <img src={r.qr} alt="QR Code" className="w-48 h-48 mx-auto rounded-xl ring-1 ring-slate-200" data-testid="pass-qr" />
          <div>
            <Row label="Nome ospite" value={r.nominativo} testid="pass-name" />
            <Row label="Data autorizzazione ingresso" value={r.periodo ? `${r.data_label} · valido ${r.periodo}` : r.data_label} testid="pass-date" />
            <Row label="Orario" value={r.turno_label} testid="pass-time" />
            <div className="py-3 border-b border-slate-100">
              <p className="text-[11px] text-slate-500"><span className="uppercase tracking-[0.18em]">Targa</span>{r.stato === "prenotato" && <span className="text-xs text-[var(--teal)] font-medium" data-testid="pass-plate-hint"> - Inserire la targa dell'auto</span>}</p>
              <p className="text-lg font-semibold text-[var(--navy)] break-words" data-testid="pass-plate">{r.targa || <span className="text-red-600 text-base">Obbligatoria prima dell'accesso</span>}</p>
              <PlateEdit r={r} />
            </div>
            <Row label="Nome Utente ospitante" value={r.nome_park} testid="pass-host" />
          </div>
          <div className={`rounded-2xl p-3 text-center font-bold ${done ? (r.stato === "effettuato" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-600") : "bg-[var(--teal)]/10 text-[var(--teal)]"}`} data-testid="pass-stato">
            {STATO[r.stato]}{r.ingresso_at && ` · ${new Date(r.ingresso_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })}`}
          </div>
          <GateAction r={r} setR={setR} />
          <div className="pt-2 space-y-4 text-center" data-testid="pass-maps-section">
            {r.luogo_url && <a href={r.luogo_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full h-12 rounded-full bg-[var(--navy)] text-white font-bold tracking-wide hover:opacity-90 transition-opacity" data-testid="pass-maps-button"><MapPin className="w-5 h-5" />TI PORTO AL PARCHEGGIO</a>}
            <p className="text-base leading-relaxed text-slate-700" data-testid="pass-terrace-info">{r.luogo_url ? <>Una volta autorizzato l'ingresso clicca sul tasto "TI PORTO AL PARCHEGGIO" per raggiungere {r.luogo_nome ? <>la <b>{r.luogo_nome}</b></> : "il parcheggio"}. </> : null}Non lasciare la tua vettura in altre aree del porto. I posti auto al piano stradale e su altre terrazze sono privati. Una volta entrati, non è possibile uscire e rientrare. Leggi il Regolamento del porto <a href="https://www.portoturisticodiroma.it/3d-flip-book/regolamento-duso-e-desercizio/" target="_blank" rel="noopener noreferrer" className="font-semibold underline text-[#0b2545]" data-testid="pass-regolamento-link">qui</a></p>
          </div>
        </>}
      </div>
    </div>
  );
}
